import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import toast from "react-hot-toast";
import _ from "lodash";
import gqlClaims from "../../gql/claims";
import gqlPreAuth from "../../gql/preAuth";
import { appContext } from "../../context/ContextProvider";
import Pagination from "../../components/Pagination";
import Filter from "./components/filters/SubmitClaimsFilter";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClaimsSubmitModal from "./components/ClaimsSubmitModal";
import ConformationModal from "../../components/ConformationModal";
import { useTranslation } from "react-i18next";
import TableSkeletonLoader from "../../common/TableLoading";
import AppButton from "../../shared/AppButton";
import AppTable from "../../shared/AppTable";
import { FaFilter } from "react-icons/fa";

function SubmitClaims(props) {
  const { setShowLoader, setShowErrorAlert } = useContext(appContext);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const currentDate = new Date();

  const [errorData, setErrorData] = useState([]);
  const [submitClaimData, setSubmitClaimData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalSubmits, setTotalSubmits] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedValues, setSelectedValues] = useState([]);

  const [item, setItem] = useState({});
  const [showFilter, setShowFilter] = useState(false);

  const [fromDate, setFromDate] = useState(
    `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-01`,
  );
  const [toDate, setToDate] = useState(
    `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0,
    ).getDate()}`,
  );

  const [limit, setLimit] = useState(15);
  const [pageNo, setPageNo] = useState(1);
  const [refetchFilterVariables, setRefetchFilterVariables] = useState();
  const [submitOpen, setSubmitOpen] = useState(false);
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    loading: dataLoading,
    error,
    data,
    refetch,
  } = useQuery(gqlClaims.GET_PAYER_CLAIMS, {
    variables: {
      input: {
        from_date: fromDate,
        to_date: toDate,
        status: "Ready for submission",
        limit,
        page: pageNo,
      },
    },
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (data?.listPayerClaims?.data) {
      setSubmitClaimData(data?.listPayerClaims?.data);
      setTotalPages(data?.listPayerClaims?.pages);
      setTotalSubmits(data?.listPayerClaims?.total);
    }
  }, [data]);

  const [
    submitClaims,
    { data: addData, loading: addLoading, error: addError },
  ] = useMutation(gqlClaims.SUBMIT_CLAIMS, {
    update(proxy, result) {
      onSuccessfulRequestResponse(result.data);
    },
    onError(error) {
      onResponseError(error.graphQLErrors[0]);
    },
  });

  const onSuccessfulRequestResponse = (data) => {
    if (!data.submitClaims.nphies_err) {
      toast.success(t("Successfully Requested!"));
    } else {
      setErrorData(data?.submitClaims?.nphies_err);
      setShowErrorAlert(true);
    }
  };

  const onResponseError = (error) => {
    toast.error(`${error.message}`);
  };

  const onPageChange = async (currentPage) => {
    setPageNo(currentPage);
    const filterInput = {
      ...refetchFilterVariables,
      limit,
      page: currentPage,
      from_date: fromDate,
      to_date: toDate,
      status: "Ready for submission",
    };
    try {
      setLoading(true);
      await refetch({ input: filterInput }).then((res) => {
        if (res?.data?.listPayerClaims?.data) {
          setSubmitClaimData(res?.data?.listPayerClaims?.data);
          setTotalPages(res?.data?.listPayerClaims?.pages);
          setTotalSubmits(res?.data?.listPayerClaims?.total);
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const onDataRowClicked = (event, item) => {
    navigate("/claims/details", {
      state: { currentItem: item, fromSubmitClaims: true },
    });
  };

  const [cancelReq, { loading: cancelLoading }] = useMutation(
    gqlPreAuth.CANCEL_REQUEST,
    {
      update(proxy, result) {
        onSuccessfulCancelRequestResponse(result.data);
      },
      onError(error) {
        onCancelResponseError(error.graphQLErrors[0]);
      },
    },
  );

  const onSuccessfulCancelRequestResponse = (data) => {
    if (data.cancelRequest) {
      toast.success("Successfully Cancelled!");
      refetch();
    }
  };

  const onCancelResponseError = (error) => {
    toast.error(`${error.message}`);
    refetch();
  };

  const cancelClicked = (item) => {
    cancelReq({
      variables: {
        input: { transaction_id: item.transaction_id, request_name: "claims" },
      },
    });
  };

  const formatDateTime = (date) => {
    const options = { year: "numeric", month: "numeric", day: "numeric" };
    return new Intl.DateTimeFormat("en-GB", options).format(new Date(date));
  };

  const formik = useFormik({
    initialValues: {
      is_batch: false,
      batch_start_date: "",
      batch_end_date: "",
    },
    validationSchema: Yup.object({
      is_batch: Yup.boolean().required(t("Is Batch Required")),
      batch_start_date: Yup.string().test(
        "is-batch",
        t("Batch Start Date Required"),
        function (value) {
          return this.parent.is_batch ? !!value : true;
        },
      ),
      batch_end_date: Yup.string().test(
        "is-batch",
        t("Batch End Date Required"),
        function (value) {
          return this.parent.is_batch ? !!value : true;
        },
      ),
    }),
    onSubmit: async (values) => {
      if (submitClaimData?.length > 0 && selectedValues.length > 0) {
        toast.loading("Requesting!");
        submitClaims({
          variables: { input: { id: selectedValues, ...values } },
        }).then(console.log);
      }
    },
  });

  useEffect(() => {
    if (error?.graphQLErrors[0]?.message)
      toast.error(error?.graphQLErrors[0]?.message);
    if (addError?.graphQLErrors[0]?.message)
      toast.error(addError?.graphQLErrors[0]?.message);
  }, [error, addError]);

  const columns = [
    {
      header: t("SQ #"),
      render: (row, index) => pageNo * limit + index + 1 - limit,
    },
    {
      header: t("Claim Date"),
      render: (row) => (
        <span
          className="!whitespace-normal block"
          dangerouslySetInnerHTML={{
            __html: new Date(row?.claim_date)
              .toLocaleString("en-GB")
              .replace(", ", "<br/>"),
          }}
        />
      ),
    },
    { header: t("Type"), field: "type" },
    { header: t("Name"), field: "patient_name" },
    { header: t("Document Id"), field: "patient_document_id" },
    {
      header: t("Payer Name"),
      render: (row) => (
        <span className="text-[12px] text-gray-700 w-[270px] block whitespace-pre-wrap">
          {row?.payer_name}
        </span>
      ),
    },
    {
      header: t("Order Date"),
      render: (row) => formatDateTime(row?.date_ordered) || row?.date_ordered,
    },
    { header: t("Total Net"), field: "total_net" },
  ];

  return (
    <>
      <div className="pre_auth_container !p-0">
        {/* Action Bar */}
        <div className="flex justify-between gap-1.5 flex-col sm:flex-row mb-2">
          <div className="w-full sm:w-[calc(100%-150px)]">
            <span className="font-semibold text-black/70 text-lg ">
              {t("Submission Claims")}
              <span className=" text-lg ml-1">
                ({data?.listPayerClaims?.total || 0}{" "}
                <span className="text-xs font-bold italic">{t("Entries")}</span>
                )
              </span>
            </span>
          </div>
          {submitClaimData?.length > 0 && selectedValues.length > 0 && (
            <AppButton
              label={t("Submit Claim")}
              startIcon={<AddIcon />}
              onClick={() => setSubmitOpen(true)}
            />
          )}
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white border border-slate-200 rounded-lg p-2 mb-2">
            <Filter
              refetch={refetch}
              setPageNo={setPageNo}
              setLimit={setLimit}
              setFromDate={setFromDate}
              setToDate={setToDate}
              setLoading={setLoading}
              setRefetchFilterVariables={setRefetchFilterVariables}
              pageNo={pageNo}
              limit={limit}
              setSubmitClaimData={setSubmitClaimData}
              setTotalPages={setTotalPages}
              setTotalSubmits={setTotalSubmits}
            />
          </div>
        )}

        {/* Table */}
        <AppTable
        cellClassName="!py-0"
          selectable={true}
          selectedRows={selectedValues}
          onSelectRow={(id) => {
            if (selectedValues.includes(id)) {
              setSelectedValues(selectedValues.filter((v) => v !== id));
            } else {
              setSelectedValues([...selectedValues, id]);
            }
          }}
          onSelectAll={(checked) => {
            if (checked && submitClaimData) {
              setSelectedValues(submitClaimData.map((row) => row.id));
            } else {
              setSelectedValues([]);
            }
          }}
          columns={columns}
          data={submitClaimData}
          loading={loading || dataLoading || addLoading}
          onView={(row) => onDataRowClicked(null, row)}
          onEdit={(row) => navigate("/claims/edit/form/" + row.transaction_id)}
          onDelete={(row) => {
            setPendingDeleteItem(row);
            setShowDeleteConfirm(true);
          }}
          LoaderComponent={
            <TableSkeletonLoader
              columnCount={columns.length + 2}
              rowCount={6}
            />
          }
        />

        {/* Pagination */}
        <div className="">
          <Pagination
            currentPage={pageNo}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      <ConformationModal
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        onDelete={() => {
          if (pendingDeleteItem) {
            cancelClicked(pendingDeleteItem);
            setPendingDeleteItem(null);
          }
          setShowDeleteConfirm(false);
        }}
        text={t("Cancel")}
      />

      <ClaimsSubmitModal
        open={submitOpen}
        setOpen={setSubmitOpen}
        handleClose={() => setSubmitOpen(false)}
        handleClickOpen={() => setSubmitOpen(true)}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        refetch={refetch}
        data={data}
        setSubmitClaimData={setSubmitClaimData}
        setTotalPages={setTotalPages}
        setTotalSubmits={setTotalSubmits}
      />
    </>
  );
}

export default SubmitClaims;
